import { ref, computed } from 'vue';

// Global translations passed from backend
const globalTranslations = ref<Record<string, string>>({});

// Parse ICU MessageFormat plural
const parsePlural = (message: string, params: Record<string, any>): string => {
  // Match {count, plural, one{...} few{...} many{...} other{...}}
  const pluralRegex = /\{(\w+),\s*plural,\s*((?:\w+\{[^}]*\}\s*)+)\}/g;
  
  return message.replace(pluralRegex, (match, paramName, forms) => {
    const count = parseInt(params[paramName], 10) || 0;
    
    // Extract each form: one{...}, few{...}, many{...}, other{...}
    const formRegex = /(\w+)\{([^}]*)\}/g;
    const formMap: Record<string, string> = {};
    let formMatch;
    
    while ((formMatch = formRegex.exec(forms)) !== null) {
      formMap[formMatch[1]] = formMatch[2];
    }
    
    // Determine which form to use based on count and language
    const lang = document.documentElement.lang || 'en';
    let formKey = 'other';
    
    if (lang === 'ru') {
      // Russian plural rules
      const mod10 = count % 10;
      const mod100 = count % 100;
      
      if (mod10 === 1 && mod100 !== 11) {
        formKey = 'one';
      } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
        formKey = 'few';
      } else if (formMap.many) {
        formKey = 'many';
      } else {
        formKey = 'other';
      }
    } else {
      // English and default
      if (count === 1) {
        formKey = 'one';
      } else {
        formKey = 'other';
      }
    }
    
    // Get the appropriate form
    let result = formMap[formKey] || formMap.other || '';
    
    // Replace # with count
    result = result.replace(/#/g, String(count));
    
    return result;
  });
};

export function useI18n() {
  const t = (key: string, params: Record<string, any> = {}): string => {
    let translation = globalTranslations.value[key] || key;
    
    // Handle ICU MessageFormat plural
    if (translation.includes('{') && translation.includes('plural')) {
      translation = parsePlural(translation, params);
    }
    
    // Simple parameter replacement for remaining {param}
    let result = translation;
    Object.entries(params).forEach(([param, value]) => {
      result = result.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
    });
    
    return result;
  };

  const setTranslations = (translations: Record<string, string>) => {
    globalTranslations.value = translations;
  };

  const currentLanguage = computed(() => {
    return document.documentElement.lang || 'en';
  });

  return {
    t,
    setTranslations,
    currentLanguage,
  };
}

// Global instance for non-composable usage
const globalI18n = useI18n();
export { globalI18n as i18n };
